import { useMemo, useState, useRef, CSSProperties } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { TextField, ListSubheader, MenuItem, ListItemIcon, ListItemText, Stack, Typography, Button, Box, Tooltip, Skeleton, MenuList } from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { cloneDeep } from "lodash";
import { List, type RowComponentProps } from "react-window";
import ManageSearchRoundedIcon from "@mui/icons-material/ManageSearchRounded";
import { AppState } from "../../../../../store/types";
import { User, Instance } from "../../../../../services/types";
import { useGetInstancesQuery } from "../../../../../services/accounts";
import noSearchResults from "../../../../../../../public/images/noSearchResults.svg";
import { CascadingMenuItem } from "../../../../CascadingMenuItem";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
interface ListRowData {
  type: "header" | "data" | "skeleton";
  data?: Instance;
  headerText?: string;
  // Stable, locale-independent discriminator for the header rows. Used for the
  // icon logic in Row() so it never depends on the (translated) headerText.
  headerKey?: "favorites" | "all";
}
export const InstancesList = () => {
  const {
    t
  } = useTranslation();
  const [filter, setFilter] = useState("");
  const searchField = useRef<HTMLInputElement | null>(null);
  const user: User = useSelector((state: AppState) => state.user);
  const {
    data: instances,
    isLoading
  } = useGetInstancesQuery();
  const [menuFocused, setMenuFocused] = useState(false);
  const handleArrowNavigation = (e: React.KeyboardEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (e.key === "ArrowDown" && !menuFocused) {
      const menuItem = document.querySelector(".MuiListSubheader-root + div li:first-of-type");
      (menuItem as HTMLElement)?.focus();
      setMenuFocused(true);
    }
  };
  const favoriteInstances = useMemo(() => {
    if (user && instances?.length) {
      let data: Instance[] = [];
      if (user?.prefs) {
        const faveSites: string[] = JSON.parse(user?.prefs)?.favorite_sites;
        faveSites?.forEach(fav => {
          const res = instances?.filter(instance => instance.ZUID === fav);
          data.push(...res);
        });
      }
      return data;
    }
    return [];
  }, [user, instances]);
  const filteredFavoriteInstances = useMemo(() => {
    if (filter) {
      return favoriteInstances?.filter(site => site.name?.toLowerCase().includes(filter.toLowerCase()));
    }
    return favoriteInstances;
  }, [filter, favoriteInstances]);
  const filteredInstances = useMemo(() => {
    const _instances = cloneDeep(instances);
    const sortedInstances = _instances?.sort((a, b) => a.name?.localeCompare(b.name));
    if (filter) {
      return sortedInstances?.filter(instance => instance.name?.toLowerCase().includes(filter.toLowerCase()));
    }
    return sortedInstances;
  }, [filter, instances]);
  const listData = useMemo(() => {
    let faveInstances: ListRowData[] = [];
    let allInstances: ListRowData[] = [];
    if (filteredFavoriteInstances?.length) {
      faveInstances = filteredFavoriteInstances?.map(instance => ({
        type: "data" as "header" | "data",
        data: instance
      }));
    }
    if (isLoading) {
      faveInstances = [...Array(5)].map(() => ({
        type: "skeleton"
      }));
      allInstances = [...Array(10)].map(() => ({
        type: "skeleton"
      }));
    }
    if (filteredInstances?.length) {
      allInstances = filteredInstances?.map(instance => ({
        type: "data" as "header" | "data",
        data: instance
      }));
    }
    return [{
      type: "header",
      headerKey: "favorites",
      headerText: t("shell.favorites")
    }, ...faveInstances, {
      type: "header",
      headerKey: "all",
      headerText: t("shell.allInstances")
    }, ...allInstances];
  }, [filteredInstances, filteredFavoriteInstances, t]);
  const handleResetFilter = () => {
    setFilter("");
    searchField.current?.focus();
  };
  return <CascadingMenuItem data-cy="InstanceSwitcher" MenuItemComponent={<>
          <ListItemIcon>
            <ManageSearchRoundedIcon />
          </ListItemIcon>
          <ListItemText>{t("shell.switchInstance")}</ListItemText>
          <ArrowForwardIosRoundedIcon color="action" fontSize="small" />
        </>} onKeyDown={handleArrowNavigation} PopperProps={{
    popperOptions: {
      modifiers: [{
        name: "offset",
        options: {
          offset: [-16, -8]
        }
      }]
    }
  }} PaperProps={{
    sx: {
      /**
       * HACK: Compute min-height depending on how many data are to be rendered (36px each) + search box (72px) + margin top & bottom (16px).
       * Virtualized list won't render when using %-based heights.
       * Maxes out at 540px.
       */
      height: filteredInstances?.length ? `min(${16 + 72 + 36 * listData?.length}px, 540px)` : 445,
      width: 340,
      borderRadius: "8px"
    }
  }} data-sentry-element="CascadingMenuItem" data-sentry-component="InstancesList" data-sentry-source-file="InstancesList.tsx">
      <Stack flexDirection="column" height="inherit" data-cy="InstancesList" data-sentry-element="Stack" data-sentry-source-file="InstancesList.tsx">
        <ListSubheader sx={{
        p: 2,
        borderBottom: "1px solid",
        borderColor: "border",
        height: 72,
        borderRadius: "8px 8px 0px 0px"
      }} data-sentry-element="ListSubheader" data-sentry-source-file="InstancesList.tsx">
          <TextField autoFocus fullWidth placeholder={t("shell.searchInstances")} value={filter} onChange={e => setFilter(e.target.value)} inputRef={searchField} onFocus={() => {
          setMenuFocused(false);
        }} onKeyDown={(e: React.KeyboardEvent) => {
          const allowedKeys = ["ArrowUp", "ArrowDown", "Escape"];
          if (!allowedKeys.includes(e.key)) {
            e.stopPropagation();
          }
        }} data-sentry-element="TextField" data-sentry-source-file="InstancesList.tsx" />
        </ListSubheader>
        {filter && !filteredInstances?.length ? <Stack px={2} justifyContent="center" alignItems="center" textAlign="center" height={445}>
            <img src={noSearchResults} alt={t("shell.noSearchResultsAlt")} width={88} height={80} />
            <Typography variant="h5" fontWeight={600}>
              {t("shell.searchNoResults", {
            query: filter
          })}
            </Typography>
            <Typography variant="body2" color="text.secondary" pb={3}>
              {t("shell.searchNoResultsSuggestion")}
            </Typography>
            <Button variant="contained" onClick={handleResetFilter} startIcon={<SearchRoundedIcon />}>
              {t("common.searchAgain")}
            </Button>
          </Stack> : <MenuList sx={{
        height: "445px",
        marginTop: "8px",
        marginBottom: "8px",
        padding: 0
      }}>
            <List rowCount={listData?.length} rowHeight={36} rowProps={{
          data: listData
        }} rowComponent={Row} />
          </MenuList>}
      </Stack>
    </CascadingMenuItem>;
};
interface ListRowProps {
  data: ListRowData[];
}
const Row = ({
  index,
  data,
  style
}: any) => {
  const instance = data[index];
  const handleSwitchInstance = (ZUID: string) => {
    window.location.href = `${CONFIG.URL_MANAGER_PROTOCOL}${ZUID}${CONFIG.URL_MANAGER}`;
  };
  if (instance.type === "skeleton") {
    return <MenuItem sx={style}>
        <Skeleton width="100%" height={36} />
      </MenuItem>;
  }
  if (instance.type === "header") {
    const isFave = instance.headerKey === "favorites";
    const isAll = instance.headerKey === "all";
    return <MenuItem disableRipple sx={{
      borderTop: isAll ? 1 : 0,
      borderColor: "border",
      "&:hover": {
        cursor: "default",
        backgroundColor: "transparent"
      },
      ...style
    }}>
        <ListItemIcon>
          {isFave && <StarIcon />}
          {isAll && <GridViewRoundedIcon />}
        </ListItemIcon>
        <ListItemText>{instance.headerText}</ListItemText>
      </MenuItem>;
  }
  if (instance.type === "data") {
    return <MenuItem key={instance.data.ZUID} onClick={() => handleSwitchInstance(instance.data.ZUID)} sx={style}>
        <Tooltip title={instance.data.name} enterDelay={500} enterNextDelay={500}>
          <ListItemText primaryTypographyProps={{
          noWrap: true
        }}>
            {instance.data.name}
          </ListItemText>
        </Tooltip>
      </MenuItem>;
  }
};